import * as React from 'react';
import { omit, toArray } from '@rc-component/util';
import { clsx } from 'clsx';
import { useMergeSemantic, useSemanticRootStyle } from '../_util/hooks/useMergeSemantic';
import { devUseWarning } from '../_util/warning';
import { useComponentConfig } from '../config-provider/context';
import useSize from '../config-provider/hooks/useSize';
import useVariant from '../form/hooks/useVariants';
import Skeleton from '../skeleton';
import Tabs from '../tabs';
import CardGrid from './CardGrid';
import useStyle from './style';
const ActionNode = (props) => {
    const { actionClasses, actions = [], actionStyle } = props;
    return (React.createElement("ul", { className: actionClasses, style: actionStyle }, actions.map((action, index) => {
        // Move this out since eslint not allow index key
        // And eslint-disable makes conflict with rollup
        // ref https://github.com/ant-design/ant-design/issues/46022
        const key = `action-${index}`;
        return (React.createElement("li", { style: { width: `${100 / actions.length}%` }, key: key },
            React.createElement("span", null, action)));
    })));
};
const Card = React.forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, className, rootClassName, style, extra, headStyle = {}, bodyStyle = {}, title, loading, bordered, variant: customVariant, size: customizeSize, type, cover, actions, tabList, children, activeTabKey, defaultActiveTabKey, tabBarExtraContent, hoverable, tabProps = {}, classNames, styles, ...rest } = props;
    const { getPrefixCls, direction, className: contextClassName, style: contextStyle, classNames: contextClassNames, styles: contextStyles, } = useComponentConfig('card');
    const [variant] = useVariant('card', customVariant, bordered);
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('Card');
        warning.deprecated(customizeSize !== 'default', 'size="default"', 'size="medium"');
    }
    const mergedSize = useSize(customizeSize);
    // =========== Merged Props for Semantic ==========
    const mergedProps = {
        ...props,
        size: mergedSize,
        variant: variant,
    };
    const contextStyleRoot = useSemanticRootStyle(contextStyle);
    const styleRoot = useSemanticRootStyle(style);
    const [mergedClassNames, mergedStyles] = useMergeSemantic([contextClassNames, classNames], [contextStyles, contextStyleRoot, styles, styleRoot], {
        props: mergedProps,
    });
    // =================Warning===================
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('Card');
        [
            ['headStyle', 'styles.header'],
            ['bodyStyle', 'styles.body'],
            ['bordered', 'variant'],
        ].forEach(([deprecatedName, newName]) => {
            warning.deprecated(!(deprecatedName in props), deprecatedName, newName);
        });
    }
    const onTabChange = (key) => {
        props.onTabChange?.(key);
    };
    const childNodes = React.useMemo(() => toArray(children), [children]);
    const isContainGrid = React.useMemo(() => childNodes.some((child) => React.isValidElement(child) && child.type === CardGrid), [childNodes]);
    const prefixCls = getPrefixCls('card', customizePrefixCls);
    const [hashId, cssVarCls] = useStyle(prefixCls);
    const loadingBlock = (React.createElement(Skeleton, { loading: true, active: true, paragraph: { rows: 4 }, title: false }, children));
    const hasActiveTabKey = activeTabKey !== undefined;
    const extraProps = {
        ...tabProps,
        [hasActiveTabKey ? 'activeKey' : 'defaultActiveKey']: hasActiveTabKey
            ? activeTabKey
            : defaultActiveTabKey,
        tabBarExtraContent,
    };
    let head;
    const tabSize = mergedSize !== 'small' ? 'large' : mergedSize;
    const tabs = tabList ? (React.createElement(Tabs, { size: tabSize, ...extraProps, className: `${prefixCls}-head-tabs`, onChange: onTabChange, items: tabList.map(({ tab, ...item }) => ({ label: tab, ...item })) })) : null;
    if (title || extra || tabs) {
        const headClasses = clsx(`${prefixCls}-head`, mergedClassNames.header);
        const titleClasses = clsx(`${prefixCls}-head-title`, mergedClassNames.title);
        const extraClasses = clsx(`${prefixCls}-extra`, mergedClassNames.extra);
        const mergedHeadStyle = {
            ...headStyle,
            ...mergedStyles.header,
        };
        head = (React.createElement("div", { className: headClasses, style: mergedHeadStyle },
            React.createElement("div", { className: `${prefixCls}-head-wrapper` },
                title && (React.createElement("div", { className: titleClasses, style: mergedStyles.title }, title)),
                extra && (React.createElement("div", { className: extraClasses, style: mergedStyles.extra }, extra))),
            tabs));
    }
    const coverClasses = clsx(`${prefixCls}-cover`, mergedClassNames.cover);
    const coverDom = cover ? (React.createElement("div", { className: coverClasses, style: mergedStyles.cover }, cover)) : null;
    const bodyClasses = clsx(`${prefixCls}-body`, mergedClassNames.body);
    const mergedBodyStyle = {
        ...bodyStyle,
        ...mergedStyles.body,
    };
    const body = loading || childNodes.length ? (React.createElement("div", { className: bodyClasses, style: mergedBodyStyle }, loading ? loadingBlock : children)) : null;
    const actionClasses = clsx(`${prefixCls}-actions`, mergedClassNames.actions);
    const actionDom = actions?.length ? (React.createElement(ActionNode, { actionClasses: actionClasses, actionStyle: mergedStyles.actions, actions: actions })) : null;
    const divProps = omit(rest, ['onTabChange']);
    const classString = clsx(prefixCls, contextClassName, {
        [`${prefixCls}-loading`]: loading,
        [`${prefixCls}-bordered`]: variant !== 'borderless',
        [`${prefixCls}-hoverable`]: hoverable,
        [`${prefixCls}-contain-grid`]: isContainGrid,
        [`${prefixCls}-contain-tabs`]: tabList?.length,
        [`${prefixCls}-small`]: mergedSize === 'small',
        [`${prefixCls}-type-${type}`]: !!type,
        [`${prefixCls}-rtl`]: direction === 'rtl',
    }, className, rootClassName, hashId, cssVarCls, mergedClassNames.root);
    const mergedStyle = {
        ...mergedStyles.root,
    };
    return (React.createElement("div", { ref: ref, ...divProps, className: classString, style: mergedStyle },
        head,
        coverDom,
        body,
        actionDom));
});
if (process.env.NODE_ENV !== 'production') {
    Card.displayName = 'Card';
}
export default Card;
